import { MIN_TOUCH_TARGET_PX, cn } from '@powerful/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Esqueleto partilhado pelos dois shells — a **base de acessibilidade EAA** (🔴 C2).
 *
 * Diretiva (UE) 2019/882 → EN 301 549 v3.2.1 → WCAG 2.1 AA.
 *
 * Existe separado dos shells porque *landmarks* e *skip link* não são detalhe de cada contexto: são
 * a estrutura que um leitor de ecrã usa para se orientar em **qualquer** tela. Repeti-los em cada
 * shell garantiria que um deles ficaria para trás — e a falha seria invisível para quem não usa
 * leitor de ecrã.
 */

/** Alvo do *skip link*. Fora do componente porque o E2E de teclado o referencia. */
export const CONTEUDO_ID = 'conteudo';

export interface AppShellProps {
  /** Barra superior opcional (contexto, switcher no M1). */
  header?: ReactNode;
  /** Navegação principal — *bottom tabs* ou *sidebar*, conforme o shell e o viewport. */
  nav?: ReactNode;
  /** Conteúdo da rota ativa. */
  children: ReactNode;
  /** Nome acessível do `<main>`, quando a tela precisa de o distinguir. */
  mainLabel?: string;
  className?: string;
}

export function AppShell({ header, nav, children, mainLabel, className }: AppShellProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex min-h-dvh flex-col bg-bg text-ink-1', className)}>
      {/*
        O skip link é o PRIMEIRO elemento focável do documento, por exigência do WCAG 2.4.1
        (Bypass Blocks). Sem ele, quem navega por teclado percorre os cinco separadores em TODAS as
        telas antes de chegar ao conteúdo — o que é tecnicamente navegável e praticamente inutilizável.

        Fica escondido até receber foco: `sr-only` tira-o do fluxo visual, e `focus:not-sr-only`
        devolve-o. Escondê-lo com `display:none` seria pior que não o ter, porque o removeria também
        da ordem de tabulação.
      */}
      <a
        href={`#${CONTEUDO_ID}`}
        /*
          `minHeight` inline vence a corrida de especificidade contra o reset de `height:auto` que
          o `focus:not-sr-only` aplica — ver o comentário abaixo. O valor vem da constante
          partilhada com o `Button` (T0.2), nunca de um `44` solto.

          Só `minHeight`: `display` fica de fora porque o `sr-only` depende do seu próprio
          (`position:absolute` + `clip`), e sobrepô-lo inline tiraria o link do fluxo de esconder.
          Para centrar o texto na caixa de 44 px basta o `leading` do `py-3`.
        */
        style={{ minHeight: `${MIN_TOUCH_TARGET_PX}px` }}
        className={cn(
          'sr-only focus:not-sr-only',
          'focus:absolute focus:left-4 focus:top-4 focus:z-50',
          /*
            O alvo de 44 px (`RNF-ACC-05`) vem por `style` e não por classe utilitária.

            Motivo: `focus:not-sr-only` repõe `height:auto`, e como ambas as regras nascem com a
            mesma especificidade, quem vence é a ordem no ficheiro gerado — que o Tailwind decide,
            não nós. O `padding` sozinho dava **20 px de altura**, e foi o E2E de alvos, medindo
            píxeis reais, que o apanhou.

            Um skip link abaixo do mínimo falha justamente para quem tem dificuldade motora — o
            mesmo público que mais depende dele.
          */
          'focus:box-border focus:rounded-md focus:bg-surface-2 focus:px-4 focus:py-3',
          'focus:text-ink-1 focus:outline-none focus:ring-2 focus:ring-gold',
        )}
      >
        {t('nav.skip')}
      </a>

      {header}

      {/*
        `id` e `tabIndex={-1}` juntos: o `id` é o destino do salto, e o `tabIndex` negativo torna o
        `<main>` focável POR PROGRAMA sem entrar na ordem de tabulação. Sem ele, o browser move a
        âncora mas não o foco do teclado — o utilizador salta visualmente e continua a tabular a
        partir da navegação, que é exatamente o que o skip link deveria ter evitado.
      */}
      <main
        id={CONTEUDO_ID}
        tabIndex={-1}
        aria-label={mainLabel}
        className="flex-1 focus:outline-none"
      >
        {children}
      </main>

      {nav}
    </div>
  );
}
